import React from 'react';
import { LTTooltip } from '@lambdatestincprivate/lt-components';
import { expectedAnswerLabel } from '../../model/coverage';
import { usePackState } from '../../data/pack-store';
import { openDetailWithUrl } from '../../app/url-sync';
import { truncateMiddle } from '../format';
import { StatusIcon } from '../icons/StatusIcon';
import { ScenarioIcon } from './CoverageIcons';
import styles from './coverage.module.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function riskClass(risk) {
  if (risk === 'high') return styles.riskHigh;
  if (risk === 'med' || risk === 'medium') return styles.riskMed;
  return styles.riskLow;
}

/** Execution is THIS pack's verdict — a different axis than the score.
 * blocked / not-run are no-signal: neutral, never red (handoff rule 2). */
function ExecutionIcon(_ref) {
  var execution = _ref.execution;
  if (execution === 'passed') return /*#__PURE__*/_jsx(StatusIcon, {
    status: "passed",
    size: 20
  });
  if (execution === 'failed') return /*#__PURE__*/_jsx(StatusIcon, {
    status: "failed",
    size: 20
  });
  return /*#__PURE__*/_jsx(StatusIcon, {
    status: "skipped",
    size: 20
  });
}
var VerifyIcon = function VerifyIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("circle", {
      cx: "8",
      cy: "8",
      r: "6.4",
      stroke: "#6E7781",
      strokeWidth: "1.25"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "8",
      cy: "8",
      r: "3.7",
      stroke: "#6E7781",
      strokeWidth: "1.25"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "8",
      cy: "8",
      r: "1",
      fill: "#6E7781"
    })]
  });
};

/**
 * One acceptance-criteria card (Figma 30786-12370): status icon | main
 * column (title, meta, Verify box) | score rail. Facts only: the score
 * and verified tick come from the ACTIVE aspect's rollup; nothing is
 * computed here.
 */
export function AcRow(_ref2) {
  var _ac$expectedAnswer, _ac$text, _score$score, _score$passes, _score$counted, _score$score2, _score$score3;
  var ac = _ref2.ac,
    score = _ref2.score,
    usedIn = _ref2.usedIn;
  var rows = usePackState(function (s) {
    return s.rows;
  });
  var expected = expectedAnswerLabel(ac.expectedAnswer);
  var statement = (_ac$expectedAnswer = ac.expectedAnswer) === null || _ac$expectedAnswer === void 0 ? void 0 : _ac$expectedAnswer.statement;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.acRow,
    children: [/*#__PURE__*/_jsx("span", {
      className: styles.acIcon,
      children: /*#__PURE__*/_jsx(ExecutionIcon, {
        execution: ac.execution
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.acMain,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.acText,
        children: (_ac$text = ac.text) !== null && _ac$text !== void 0 ? _ac$text : ac.id
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.acMeta,
        children: [/*#__PURE__*/_jsx(LTTooltip, {
          text: ac.id,
          direction: "bottom",
          children: /*#__PURE__*/_jsx("span", {
            className: styles.idChip,
            children: truncateMiddle(ac.id, 24)
          })
        }), ac.risk && /*#__PURE__*/_jsx("span", {
          className: "".concat(styles.riskChip, " ").concat(riskClass(ac.risk)),
          children: ac.risk
        }), ac.satisfiedBy.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.metaDot
          }), /*#__PURE__*/_jsx("span", {
            children: "Verified by"
          }), ac.satisfiedBy.map(function (s) {
            var _s$step, _s$step2;
            return rows.has(s.test) ? /*#__PURE__*/_jsx("button", {
              type: "button",
              className: styles.testLink,
              onClick: function onClick() {
                return openDetailWithUrl(s.test);
              },
              children: s.test
            }, "".concat(s.test, "-").concat((_s$step = s.step) !== null && _s$step !== void 0 ? _s$step : '')) :
            /*#__PURE__*/
            // Test isn't in this pack's rows (e.g. cross-pack slug) —
            // still a fact, just not navigable here.
            _jsx("span", {
              className: styles.testPlain,
              children: s.test
            }, "".concat(s.test, "-").concat((_s$step2 = s.step) !== null && _s$step2 !== void 0 ? _s$step2 : ''));
          })]
        }), ac.coveredBy.length === 0 && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.metaDot
          }), /*#__PURE__*/_jsx("span", {
            children: "no covering test"
          })]
        }), ac.executions.some(function (e) {
          return e.viaParent;
        }) && /*#__PURE__*/_jsx("span", {
          className: styles.viaBadge,
          children: "via parent"
        }), ac.executions.some(function (e) {
          return e.oracleMode === 'runtime-derived';
        }) && /*#__PURE__*/_jsx("span", {
          className: styles.oracleBadge,
          title: "Unconfirmed oracle \u2014 strict scoring discounts these runs",
          children: "runtime-derived"
        })]
      }), usedIn && usedIn.length > 0 && /*#__PURE__*/_jsxs("div", {
        className: styles.acUsedRow,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.acUsedLabel,
          children: "Used in"
        }), usedIn.map(function (sc) {
          var _sc$intent;
          return /*#__PURE__*/_jsx(LTTooltip, {
            text: (_sc$intent = sc.intent) !== null && _sc$intent !== void 0 ? _sc$intent : sc.id,
            direction: "bottom",
            children: /*#__PURE__*/_jsxs("span", {
              className: styles.scenarioChip,
              children: [/*#__PURE__*/_jsx("span", {
                className: styles.scenarioChipIcon,
                "aria-hidden": "true",
                children: /*#__PURE__*/_jsx(ScenarioIcon, {})
              }), truncateMiddle(sc.id, 24)]
            })
          }, sc.id);
        })]
      }), statement && /*#__PURE__*/_jsxs("div", {
        className: styles.verifyRow,
        children: [/*#__PURE__*/_jsxs("span", {
          className: styles.verifyLabel,
          children: [/*#__PURE__*/_jsx(VerifyIcon, {}), "Verify"]
        }), /*#__PURE__*/_jsxs("span", {
          className: styles.verifyText,
          children: [statement, expected ? " \u2014 ".concat(expected) : '']
        })]
      })]
    }), score && /*#__PURE__*/_jsxs("span", {
      className: styles.acScoreRail,
      title: "score ".concat((_score$score = score.score) !== null && _score$score !== void 0 ? _score$score : '—', " \xB7 ").concat((_score$passes = score.passes) !== null && _score$passes !== void 0 ? _score$passes : 0, " of ").concat((_score$counted = score.counted) !== null && _score$counted !== void 0 ? _score$counted : 0, " counted runs passed").concat(score.verified ? ' · verified' : ''),
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.acScoreBar,
        children: /*#__PURE__*/_jsx("span", {
          className: styles.acScoreFill,
          style: {
            width: "".concat(Math.round(((_score$score2 = score.score) !== null && _score$score2 !== void 0 ? _score$score2 : 0) * 100), "%"),
            display: 'block',
            background: score.verified ? '#1f883d' : '#9a6700'
          }
        })
      }), /*#__PURE__*/_jsx("span", {
        className: styles.acScoreVal,
        style: {
          color: score.verified ? '#1a7f37' : '#9a6700'
        },
        children: (_score$score3 = score.score) !== null && _score$score3 !== void 0 ? _score$score3 : '—'
      })]
    })]
  });
}